/**
 * V38.04 — Panneau de gestion des plans vitrine front-office (Super Admin).
 *
 * Permet de gérer (créer, modifier, supprimer, masquer/afficher, réordonner)
 * les cartes de tarification affichées en public (page /adhesion + /offre-coppam).
 *
 * Distinct de `GestionPlans.js` (qui gère les plans d'abonnement Stripe).
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Eye, EyeOff, Pencil, Trash2, Plus, ArrowUp, ArrowDown, Star, Save, X, Tag, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEED_CODES = ['porteur', 'salarie', 'entreprise', 'partenaire', 'coppam_monthly'];

const EMPTY_PLAN = {
  code: '',
  title: '',
  description: '',
  price: 0,
  price_label: '',
  period: '/an',
  currency: 'EUR',
  benefits: [],
  popular: false,
  visible: true,
  sort_order: 100,
  credits_per_month: 0,
  stripe_price_id: '',
  gradient: 'from-blue-500 to-cyan-500',
  icon: 'Sparkles',
  cta_label: 'Sélectionner',
  cta_url: '',
};

const getAuthHeader = () => {
  const t = localStorage.getItem('token')
        || localStorage.getItem('access_token')
        || localStorage.getItem('admin_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function FrontOfficePlansPanel() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [benefitInput, setBenefitInput] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/frontoffice-plans`, { headers: getAuthHeader() });
      setPlans(res.data?.plans || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur chargement plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlans(); }, [fetchPlans]);

  const openCreateDialog = () => {
    setEditingPlan({ ...EMPTY_PLAN, _mode: 'create' });
    setBenefitInput('');
    setDialogOpen(true);
  };

  const openEditDialog = (plan) => {
    setEditingPlan({ ...plan, _mode: 'edit' });
    setBenefitInput('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingPlan(null);
    setBenefitInput('');
  };

  const addBenefit = () => {
    const v = (benefitInput || '').trim();
    if (!v) return;
    setEditingPlan((p) => ({ ...p, benefits: [...(p.benefits || []), v] }));
    setBenefitInput('');
  };

  const removeBenefit = (idx) => {
    setEditingPlan((p) => ({ ...p, benefits: (p.benefits || []).filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!editingPlan) return;
    if (!editingPlan.title?.trim()) { toast.error('Titre requis'); return; }
    if (editingPlan._mode === 'create' && !editingPlan.code?.trim()) { toast.error('Code requis'); return; }

    setSaving(true);
    try {
      const payload = { ...editingPlan };
      delete payload._mode;
      delete payload.id;
      delete payload.is_seed;
      delete payload.created_at;
      delete payload.updated_at;
      // Nettoyage
      payload.price = Number(payload.price || 0);
      payload.sort_order = Number(payload.sort_order || 0);
      payload.credits_per_month = Number(payload.credits_per_month || 0);
      payload.price_label = payload.price_label || null;
      payload.stripe_price_id = payload.stripe_price_id || null;
      payload.cta_url = payload.cta_url || null;

      if (editingPlan._mode === 'create') {
        await axios.post(`${API}/admin/frontoffice-plans`, payload, { headers: getAuthHeader() });
        toast.success(`Plan « ${editingPlan.title} » créé`);
      } else {
        delete payload.code; // code immuable en update
        await axios.put(`${API}/admin/frontoffice-plans/${editingPlan.id}`, payload, { headers: getAuthHeader() });
        toast.success(`Plan « ${editingPlan.title} » mis à jour`);
      }
      closeDialog();
      await fetchPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const toggleVisible = async (plan) => {
    try {
      await axios.put(
        `${API}/admin/frontoffice-plans/${plan.id}`,
        { visible: !plan.visible },
        { headers: getAuthHeader() },
      );
      toast.success(`Plan ${!plan.visible ? 'affiché' : 'masqué'}`);
      await fetchPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (plan) => {
    if (SEED_CODES.includes(plan.code)) {
      toast.error(`Plan seed protégé. Utilisez « Masquer » pour le retirer du front-office.`);
      return;
    }
    if (!window.confirm(`Supprimer définitivement le plan « ${plan.title} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/frontoffice-plans/${plan.id}`, { headers: getAuthHeader() });
      toast.success('Plan supprimé');
      await fetchPlans();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const movePlan = async (plan, direction) => {
    const sorted = [...plans].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const idx = sorted.findIndex((p) => p.id === plan.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const other = sorted[swapIdx];
    const order = [
      { id: plan.id, sort_order: other.sort_order || 0 },
      { id: other.id, sort_order: plan.sort_order || 0 },
    ];
    try {
      await axios.post(`${API}/admin/frontoffice-plans/reorder`, { order }, { headers: getAuthHeader() });
      await fetchPlans();
    } catch {
      toast.error('Erreur réordonnancement');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="frontoffice-plans-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <Card data-testid="frontoffice-plans-panel">
      <CardHeader className="border-b">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <Tag className="h-5 w-5" />
              Plans Vitrine — Front-Office
            </CardTitle>
            <CardDescription>
              Gestion des cartes tarifaires affichées sur les pages publiques
              (<code>/adhesion</code> et <code>/offre-coppam</code>).
            </CardDescription>
          </div>
          <Button
            onClick={openCreateDialog}
            className="bg-amber-700 hover:bg-amber-800 text-white"
            data-testid="frontoffice-plans-create-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Nouveau plan
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-4 space-y-3">
        {plans.length === 0 ? (
          <p className="italic text-slate-500">Aucun plan défini.</p>
        ) : plans.map((plan) => (
          <div
            key={plan.id}
            className={`border rounded-lg p-4 flex flex-wrap items-start gap-4 ${plan.visible ? 'bg-white' : 'bg-slate-50 opacity-70'}`}
            data-testid={`frontoffice-plan-row-${plan.code}`}
          >
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-bold text-slate-900">{plan.title}</h4>
                <Badge variant="outline" className="font-mono text-[10px]">{plan.code}</Badge>
                {plan.popular && (
                  <Badge className="bg-amber-500 text-white text-[10px]"><Star className="h-3 w-3 mr-0.5" />Populaire</Badge>
                )}
                {!plan.visible && (
                  <Badge variant="outline" className="text-slate-500 text-[10px]">Masqué</Badge>
                )}
                {plan.is_seed && (
                  <Badge variant="outline" className="text-blue-700 text-[10px]">Seed</Badge>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-1">{plan.description}</p>
              <div className="text-sm text-slate-800 mt-2 font-semibold">
                {plan.price_label ? plan.price_label : `${plan.price} €`}
                <span className="text-slate-500 font-normal">{plan.period}</span>
                {plan.credits_per_month > 0 && (
                  <span className="ml-3 text-emerald-700">• {plan.credits_per_month} crédits/mois</span>
                )}
                <span className="ml-3 text-slate-500">• ordre {plan.sort_order}</span>
              </div>
              {(plan.benefits || []).length > 0 && (
                <ul className="text-xs text-slate-600 mt-2 grid grid-cols-2 gap-x-4 gap-y-0.5">
                  {plan.benefits.slice(0, 6).map((b, i) => (
                    <li key={i}>✓ {b}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Button size="sm" variant="outline" onClick={() => movePlan(plan, 'up')} data-testid={`frontoffice-plan-up-${plan.code}`}>
                <ArrowUp className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => movePlan(plan, 'down')} data-testid={`frontoffice-plan-down-${plan.code}`}>
                <ArrowDown className="h-3 w-3" />
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={plan.visible ? 'outline' : 'default'}
                onClick={() => toggleVisible(plan)}
                title={plan.visible ? 'Masquer du front-office' : 'Afficher en front-office'}
                data-testid={`frontoffice-plan-toggle-${plan.code}`}
              >
                {plan.visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEditDialog(plan)}
                data-testid={`frontoffice-plan-edit-${plan.code}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDelete(plan)}
                disabled={SEED_CODES.includes(plan.code)}
                className={SEED_CODES.includes(plan.code) ? '' : 'text-red-700 hover:bg-red-50'}
                data-testid={`frontoffice-plan-delete-${plan.code}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>

      {/* Dialog création / édition */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="frontoffice-plan-dialog">
          <DialogHeader>
            <DialogTitle>{editingPlan?._mode === 'create' ? 'Nouveau plan vitrine' : `Modifier : ${editingPlan?.title || ''}`}</DialogTitle>
            <DialogDescription>
              Les modifications sont immédiatement reflétées sur les pages publiques
              (rafraîchir le front-office pour voir le résultat).
            </DialogDescription>
          </DialogHeader>

          {editingPlan && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code (unique, immuable)</Label>
                  <Input
                    value={editingPlan.code || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, code: e.target.value })}
                    disabled={editingPlan._mode === 'edit'}
                    data-testid="frontoffice-plan-field-code"
                  />
                </div>
                <div>
                  <Label>Titre</Label>
                  <Input
                    value={editingPlan.title || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, title: e.target.value })}
                    data-testid="frontoffice-plan-field-title"
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={editingPlan.description || ''}
                  onChange={(e) => setEditingPlan({ ...editingPlan, description: e.target.value })}
                  rows={2}
                  data-testid="frontoffice-plan-field-description"
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Prix (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editingPlan.price ?? 0}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price: parseFloat(e.target.value) || 0 })}
                    data-testid="frontoffice-plan-field-price"
                  />
                </div>
                <div>
                  <Label>Libellé prix (optionnel)</Label>
                  <Input
                    placeholder="Sur mesure, Convention…"
                    value={editingPlan.price_label || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, price_label: e.target.value })}
                    data-testid="frontoffice-plan-field-price-label"
                  />
                </div>
                <div>
                  <Label>Période</Label>
                  <Input
                    placeholder="/an, /mois, vide"
                    value={editingPlan.period || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, period: e.target.value })}
                    data-testid="frontoffice-plan-field-period"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Crédits/mois</Label>
                  <Input
                    type="number"
                    value={editingPlan.credits_per_month ?? 0}
                    onChange={(e) => setEditingPlan({ ...editingPlan, credits_per_month: parseInt(e.target.value, 10) || 0 })}
                    data-testid="frontoffice-plan-field-credits"
                  />
                </div>
                <div>
                  <Label>Stripe Price ID</Label>
                  <Input
                    placeholder="price_..."
                    value={editingPlan.stripe_price_id || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, stripe_price_id: e.target.value })}
                    data-testid="frontoffice-plan-field-stripe"
                  />
                </div>
                <div>
                  <Label>Ordre d'affichage</Label>
                  <Input
                    type="number"
                    value={editingPlan.sort_order ?? 100}
                    onChange={(e) => setEditingPlan({ ...editingPlan, sort_order: parseInt(e.target.value, 10) || 0 })}
                    data-testid="frontoffice-plan-field-sort"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>CTA label</Label>
                  <Input
                    value={editingPlan.cta_label || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, cta_label: e.target.value })}
                    data-testid="frontoffice-plan-field-cta-label"
                  />
                </div>
                <div>
                  <Label>CTA URL</Label>
                  <Input
                    placeholder="/adhesion?college=..."
                    value={editingPlan.cta_url || ''}
                    onChange={(e) => setEditingPlan({ ...editingPlan, cta_url: e.target.value })}
                    data-testid="frontoffice-plan-field-cta-url"
                  />
                </div>
              </div>

              <div>
                <Label>Avantages (puces ✓)</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ajouter un avantage et appuyer sur Entrée"
                    value={benefitInput}
                    onChange={(e) => setBenefitInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addBenefit(); } }}
                    data-testid="frontoffice-plan-field-benefit-input"
                  />
                  <Button type="button" onClick={addBenefit} variant="outline" data-testid="frontoffice-plan-add-benefit-btn">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <ul className="mt-2 space-y-1">
                  {(editingPlan.benefits || []).map((b, i) => (
                    <li key={i} className="flex items-center justify-between text-sm border rounded px-2 py-1 bg-slate-50">
                      <span>✓ {b}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeBenefit(i)} data-testid={`frontoffice-plan-benefit-remove-${i}`}>
                        <X className="h-3 w-3" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center justify-between border rounded px-3 py-2">
                  <Label htmlFor="popular-switch" className="cursor-pointer">Badge « Populaire »</Label>
                  <Switch
                    id="popular-switch"
                    checked={!!editingPlan.popular}
                    onCheckedChange={(v) => setEditingPlan({ ...editingPlan, popular: v })}
                    data-testid="frontoffice-plan-field-popular"
                  />
                </div>
                <div className="flex items-center justify-between border rounded px-3 py-2">
                  <Label htmlFor="visible-switch" className="cursor-pointer">Visible en front-office</Label>
                  <Switch
                    id="visible-switch"
                    checked={!!editingPlan.visible}
                    onCheckedChange={(v) => setEditingPlan({ ...editingPlan, visible: v })}
                    data-testid="frontoffice-plan-field-visible"
                  />
                </div>
              </div>

              <div>
                <Label>Gradient Tailwind (icone)</Label>
                <Input
                  placeholder="from-blue-500 to-cyan-500"
                  value={editingPlan.gradient || ''}
                  onChange={(e) => setEditingPlan({ ...editingPlan, gradient: e.target.value })}
                  data-testid="frontoffice-plan-field-gradient"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} data-testid="frontoffice-plan-dialog-cancel">
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-amber-700 hover:bg-amber-800" data-testid="frontoffice-plan-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
